import {
  Autocomplete,
  TextField,
  CircularProgress,
  Checkbox,
  Backdrop,
} from "@mui/material";
import CustomTable from "../../../components/table";
import {
  downloadLoop,
  fetchCities,
  fetchCounties,
  fetchDevices,
  fetchLocations,
  fetchPartners,
  fetchTags,
} from "../../../services/loop";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import useDebounce from "../../../hooks/debounce";
import { VideoPreview } from "../../../components/modal/videopreview";

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
    "& .MuiSelect-outlined": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
  },
};

function FilterAutocomplete({
  label,
  options,
  onChange,
  value,
  getOptionLabel,
  loading,
  multiple = true,
  disabled = false,
}) {
  return (
    <Autocomplete
      disabled={disabled}
      style={{ opacity: disabled ? 0.5 : 1 }}
      sx={styles.select}
      multiple={multiple}
      options={options}
      value={value}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            label={loading ? <CircularProgress size={20} /> : label}
          />
        </>
      )}
    />
  );
}

const AddLoopStep = forwardRef(
  ({ data, campaignData, onChange, goBack, onSubmitAll, loadingbtn }, ref) => {
    const {
      selectedDevices: initialSelectedDevices = [],
      filters: initialFilters = {},
      timeInterval: initialTimeInterval = 60,
    } = data;

    const columns = [
      {
        field: "selected_devices",
        headerName: "Selectat",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => (
          <Checkbox
            style={{ opacity: loadingbtn ? 0.6 : 1 }}
            disabled={loadingbtn}
            color="black"
            checked={selectedDevices.some(
              (device) => device.device_id === params.row.device_id
            )}
            onChange={(event) =>
              handleDeviceSelect(
                params.row.device_id,
                params.row.name,
                event.target.checked
              )
            }
          />
        ),
        width: 75,
      },
      {
        field: "device_id",
        headerName: "ID Player",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          return params.row.device_id;
        },
      },
      {
        field: "device_name",
        headerName: "Nume Player",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          return params.row.name;
        },
      },
      {
        field: "partner",
        headerName: "Partener",
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          return params.row?.location?.partner?.name;
        },
      },
      {
        field: "location",
        headerName: "Locatie",
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          return params.row?.location?.name;
        },
      },
      {
        field: "county",
        headerName: "Judet",
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          return params.row?.location?.county;
        },
      },
      {
        field: "city",
        headerName: "Oras",
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => {
          return params.row?.location?.city;
        },
      },
    ];

    const [devices, setDevices] = useState([]);
    const [partners, setPartners] = useState([]);
    const [tags, setTags] = useState([]);
    const [hierarchicalData, setHierarchicalData] = useState({
      counties: [],
      cities: {},
      locations: {},
    });

    const [loading, setLoading] = useState(true);
    const [loadingFilters, setLoadingFilters] = useState({
      networks: false,
      partners: false,
      counties: false,
      cities: false,
      locations: false,
      tags: false,
    });

    const [selectedDevices, setSelectedDevices] = useState(
      initialSelectedDevices
    );
    const [timeInterval, setTimeInterval] = useState(initialTimeInterval);

    const [filters, setFilters] = useState(initialFilters);

    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const [inputText, setInputText] = useState("");
    const debouncedSearchTerm = useDebounce(inputText, 500);

    const [toastStatus, setToastStatus] = useState(false);
    const [toastDescription, setToastDescription] = useState("");

    const [loopPreviewLoading, setLoopPreviewLoading] = useState(false);

    const [allDeviceMetadata, setAllDeviceMetadata] = useState([]);

    const hasNetworkSelected = filters.networks && filters.networks.length > 0;
    const hasPartnerSelected = filters.partners && filters.partners.length > 0;

    const isCampaignValid = Boolean(
      campaignData?.name?.trim() &&
        campaignData?.client?.trim() &&
        selectedDevices.length > 0
    );

    useImperativeHandle(ref, () => ({
      saveLoopData: async () => {
        const updatedData = { selectedDevices, filters, timeInterval };
        onChange(updatedData);
        return updatedData;
      },
    }));

    const handleChangePage = async (event, newPage) => {
      const updatedFilters = { ...filters, search: debouncedSearchTerm };
      await fetchFilteredDevices(newPage + 1, true, updatedFilters);
      setPage(newPage);
      setFilters(updatedFilters);
    };

    const handleDeviceSelect = (deviceId, deviceName, checked) => {
      if (checked) {
        setSelectedDevices((prevSelected) => [
          ...prevSelected,
          { device_id: deviceId, name: deviceName },
        ]);
      } else {
        setSelectedDevices((prevSelected) =>
          prevSelected.filter((device) => device.device_id !== deviceId)
        );
      }
    };

    const fetchOptions = useCallback(async (filterType) => {
      try {
        switch (filterType) {
          case "partners":
            setLoadingFilters((prev) => ({ ...prev, partners: true }));
            const partnersResponse = await fetchPartners();
            setPartners(partnersResponse.data);
            setLoadingFilters((prev) => ({ ...prev, partners: false }));
            break;

          case "tags":
            setLoadingFilters((prev) => ({ ...prev, tags: true }));
            const tagsResponse = await fetchTags();
            setTags(tagsResponse.data);
            setLoadingFilters((prev) => ({ ...prev, tags: false }));
            break;

          case "counties":
            setLoadingFilters((prev) => ({ ...prev, counties: true }));
            const countiesResponse = await fetchCounties();
            setHierarchicalData((prev) => ({
              ...prev,
              counties: countiesResponse.data,
            }));
            setLoadingFilters((prev) => ({ ...prev, counties: false }));
            break;

          case "cities":
            setLoadingFilters((prev) => ({ ...prev, cities: true }));
            const citiesResponse = await fetchCities();
            setHierarchicalData((prev) => ({
              ...prev,
              cities: citiesResponse.data,
            }));
            setLoadingFilters((prev) => ({ ...prev, cities: false }));
            break;

          case "locations":
            setLoadingFilters((prev) => ({ ...prev, locations: true }));
            const locationsResponse = await fetchLocations();
            setHierarchicalData((prev) => ({
              ...prev,
              locations: locationsResponse.data,
            }));
            setLoadingFilters((prev) => ({ ...prev, locations: false }));
            break;
          default:
            break;
        }
      } catch (error) {
        handleStatus(error.message, setToastStatus, setToastDescription);
      }
    }, []);

    const handleStatus = (description) => {
      setToastStatus(true);
      setToastDescription(description);
      setTimeout(() => {
        setToastStatus(false);
        setToastDescription("");
      }, 5000);
    };

    const fetchFilteredDevices = async (
      pageNumber = 1,
      isLoading = true,
      updatedFilters = filters
    ) => {
      try {
        if (isLoading) setLoading(true);
        const response = await fetchDevices(updatedFilters, pageNumber);

        if (response.status !== "OK") {
          throw new Error(
            response.error || "Something went wrong, please try again later."
          );
        }

        setTotalCount(response.totalCount);
        setDevices(response.data);

        setSelectedDevices(
          response.data.map((device) => ({
            device_id: device.device_id,
            name: device.name,
          }))
        );

        setAllDeviceMetadata(
          response.data.map((d) => ({
            partnerId: d.location?.partner?.ID,
            county: d.location?.county,
            city: d.location?.city,
            locationId: d.location?.ID,
            locationName: d.location?.name,
          }))
        );
      } catch (error) {
        handleStatus(error.message);
      } finally {
        if (isLoading) setLoading(false);
      }
    };

    const updateFilters = (newFilter) => {
      const updatedFilters = { ...filters, ...newFilter };
      setFilters(updatedFilters);
      fetchFilteredDevices(1, true, updatedFilters);
    };

    const handleNetworkChange = (event, selectedNetworks) => {
      const reset = selectedNetworks.length === 0;

      updateFilters({
        networks: selectedNetworks,
        partners: [],
        counties: [],
        cities: [],
        locations: [],
      });
    };

    const handlePartnerChange = (event, selectedPartners) => {
      const partnerIds = selectedPartners.map((partner) => partner.ID);
      const resetIfNone = partnerIds.length === 0;

      updateFilters({
        partners: partnerIds,
        counties: resetIfNone ? [] : filters.counties,
        cities: resetIfNone ? [] : filters.cities,
        locations: resetIfNone ? [] : filters.locations,
      });
    };

    const getNetworkOptions = () => {
      const networks = [...new Set(partners.map((p) => p.network))];
      return networks.filter((n) => !!n);
    };

    const getFilteredPartners = () => {
      if (!filters.networks || filters.networks.length === 0) return partners;
      return partners.filter((p) => filters.networks.includes(p.network));
    };

    const handleCountyChange = (event, selectedCounties) => {
      const countyNames = selectedCounties.map((county) => county);
      updateFilters({ counties: countyNames });
    };

    const handleCityChange = (event, selectedCities) => {
      const cityNames = selectedCities.map((city) => city);
      updateFilters({ cities: cityNames });
    };

    const handleLocationChange = (event, selectedLocations) => {
      const locationIds = selectedLocations.map((location) => location.ID);
      updateFilters({ locations: locationIds });
    };

    const handleTagChange = (event, selectedTags) => {
      const tagIds = selectedTags.map((tag) => tag.ID);
      updateFilters({ tags: tagIds });
    };

    const getFilteredCounties = () => {
      const { partners = [] } = filters;

      const filtered = allDeviceMetadata.filter(
        (d) => partners.length === 0 || partners.includes(d.partnerId)
      );

      const uniqueCounties = [...new Set(filtered.map((d) => d.county))];
      return uniqueCounties;
    };

    const getFilteredCities = () => {
      const { partners = [], counties = [] } = filters;

      const filtered = allDeviceMetadata.filter(
        (d) =>
          (partners.length === 0 || partners.includes(d.partnerId)) &&
          (counties.length === 0 || counties.includes(d.county))
      );

      const uniqueCities = [...new Set(filtered.map((d) => d.city))];
      return uniqueCities;
    };

    const getFilteredLocations = () => {
      const {
        partners = [],
        counties = [],
        cities = [],
        locations = [],
      } = filters;

      const filtered = allDeviceMetadata.filter(
        (d) =>
          (partners.length === 0 || partners.includes(d.partnerId)) &&
          (counties.length === 0 || counties.includes(d.county)) &&
          (cities.length === 0 || cities.includes(d.city))
      );

      const uniqueLocations = filtered.map((d) => ({
        ID: d.locationId,
        name: d.locationName,
      }));

      const seen = new Set();
      const result = uniqueLocations.filter((loc) => {
        if (seen.has(loc.ID)) return false;
        seen.add(loc.ID);
        return true;
      });

      // Include selected locations if missing
      const selectedLocationObjects = Object.values(
        hierarchicalData.locations
      ).filter((loc) => locations.includes(loc.ID));

      selectedLocationObjects.forEach((loc) => {
        if (!seen.has(loc.ID)) {
          result.push(loc);
        }
      });

      return result;
    };

    const handleSaveAsDraft = async () => {
      try {
        await ref.current.saveLoopData();
        onSubmitAll(true); // Pass true to indicate this is a draft
      } catch (error) {
        handleStatus(error.message, setToastStatus, setToastDescription);
      }
    };

    const handleFinish = async () => {
      if (!isCampaignValid) {
        handleStatus(
          "Va rugam sa completati toate campurile necesare",
          setToastStatus,
          setToastDescription
        );
        return;
      }

      try {
        await ref.current.saveLoopData();
        onSubmitAll(false); // Pass false to indicate this is not a draft
      } catch (error) {
        handleStatus(error.message, setToastStatus, setToastDescription);
      }
    };

    const handleSelectAllDevices = () => {
      const allDevices = devices.map((dev) => ({
        device_id: dev.device_id,
        name: dev.name,
      }));

      setSelectedDevices(allDevices);
    };

    const handleDeselectAllDevices = () => {
      setSelectedDevices([]);
    };

    const handleSelectAllNetworks = () => {
      const allNetworks = getNetworkOptions();
      updateFilters({
        networks: allNetworks,
        partners: [],
        counties: [],
        cities: [],
        locations: [],
      });
    };

    const handleSelectAllPartners = () => {
      const allPartnerIds = getFilteredPartners().map((partner) => partner.ID);
      updateFilters({
        partners: allPartnerIds,
      });
    };

    useEffect(() => {
      if (debouncedSearchTerm.length < 3 && debouncedSearchTerm.length > 0) {
        return;
      }

      const updatedFilters = { ...filters, search: debouncedSearchTerm };

      setPage(0);
      fetchFilteredDevices(1, true, updatedFilters);
      setFilters(updatedFilters);
    }, [debouncedSearchTerm]);

    useEffect(() => {
      fetchFilteredDevices(1, true, {
        ...filters,
        search: debouncedSearchTerm,
      });
    }, []);

    useEffect(() => {
      fetchOptions("partners");
      fetchOptions("tags");
      fetchOptions("counties");
      fetchOptions("cities");
      fetchOptions("locations");
      fetchFilteredDevices(1);
    }, [fetchOptions]);

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{ display: "flex", alignItems: "center", alignSelf: "center" }}
        >
          <TextField
            sx={styles.select}
            style={{
              width: "7vw",
              marginBottom: 30,
            }}
            disabled={loadingbtn}
            type="number"
            value={60}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                const numericValue = parseInt(value, 10);
                if (numericValue <= 60 || value === "") {
                  setTimeInterval(value === "" ? "" : numericValue);
                }
              }
            }}
            inputProps={{
              min: 1,
              max: 60,
              step: 1,
              style: { textAlign: "center" },
            }}
            placeholder="Minutes"
          />
        </div>
        <div style={{ display: "flex", gap: 40 }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              rowGap: "30px",
              flex: "0 0 20%",
              minWidth: "200px",
              boxSizing: "border-box",
            }}
          >
            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza reteaua"
              options={getNetworkOptions()}
              value={filters.networks || []}
              onChange={handleNetworkChange}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              loading={false}
              multiple={true}
            />
            <button
              type="button"
              className="add-video-button"
              disabled={loadingbtn}
              onClick={handleSelectAllNetworks}
              style={{
                fontSize: "12px",
                padding: "8px 12px",
                marginTop: "-20px",
                marginBottom: "10px",
                opacity: loadingbtn ? 0.6 : 1,
                cursor: loadingbtn ? "not-allowed" : "pointer",
              }}
            >
              Selecteaza toate retelele
            </button>

            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza partenerul"
              options={getFilteredPartners()}
              value={
                getFilteredPartners().filter((p) =>
                  filters.partners?.includes(p.ID)
                ) || []
              }
              onChange={handlePartnerChange}
              getOptionLabel={(option) => option?.name}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={loadingFilters.partners}
              multiple={true}
            />
            <button
              type="button"
              className="add-video-button"
              disabled={loadingbtn}
              onClick={handleSelectAllPartners}
              style={{
                fontSize: "12px",
                padding: "8px 12px",
                marginTop: "-20px",
                marginBottom: "10px",
                opacity: loadingbtn ? 0.6 : 1,
                cursor: loadingbtn ? "not-allowed" : "pointer",
              }}
            >
              Selecteaza toti partenerii
            </button>

            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza judetul"
              options={hierarchicalData.counties || []}
              value={filters.counties || []}
              onChange={handleCountyChange}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              loading={loadingFilters.counties}
            />

            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza orasul"
              options={Object.values(hierarchicalData.cities) || []}
              value={filters.cities || []}
              onChange={handleCityChange}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              loading={loadingFilters.cities}
            />

            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza locatia"
              options={Object.values(hierarchicalData.locations) || []}
              value={Object.values(hierarchicalData.locations).filter((loc) =>
                filters.locations?.includes(loc.ID)
              )}
              onChange={handleLocationChange}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={loadingFilters.locations}
            />

            <FilterAutocomplete
              disabled={loadingbtn}
              label="Selecteaza tagul"
              options={tags}
              value={tags.filter((t) => filters.tags?.includes(t.ID))}
              onChange={handleTagChange}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={loadingFilters.tags}
            />
          </div>

          <div style={{ flex: 2 }}>
            <CustomTable
              search={true}
              columns={columns}
              tableLoading={loading}
              inputText={inputText}
              setInputText={setInputText}
              data={devices}
              page={page}
              handleChangePage={handleChangePage}
              totalCount={totalCount}
              addNew={true}
              addNewTitle={"Selecteaza toate playerele"}
              addNewFunction={handleSelectAllDevices}
              secondAddNew={true}
              secondAddNewTitle={"Deselecteaza toate playerele"}
              secondAddNewFunction={handleDeselectAllDevices}
              disabled={loadingbtn}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            columnGap: 10,
            marginTop: 60,
            alignSelf: "center",
          }}
        >
          <button
            type="button"
            className="add-video-button"
            style={{
              opacity: loadingbtn ? 0.6 : 1,
              cursor: loadingbtn ? "not-allowed" : "pointer",
            }}
            disabled={loadingbtn}
            onClick={() => {
              if (ref?.current?.saveLoopData) {
                ref.current.saveLoopData();
              }
              goBack();
            }}
          >
            ← Inapoi la configurarea campaniei
          </button>

          <button
            type="button"
            className="add-video-button"
            onClick={handleSaveAsDraft}
            disabled={loadingbtn}
            style={{
              opacity: loadingbtn ? 0.6 : 1,
              cursor: loadingbtn ? "not-allowed" : "pointer",
              background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
              color: "white",
              marginRight: "10px"
            }}
          >
            {loadingbtn ? (
              <CircularProgress
                size={20}
                style={{ alignSelf: "center" }}
                color="black"
              />
            ) : (
              "Salveaza ca Draft"
            )}
          </button>

          <button
            type="button"
            className="add-video-button"
            onClick={handleFinish}
            disabled={!isCampaignValid || loadingbtn}
            style={{
              opacity: !isCampaignValid || loadingbtn ? 0.6 : 1,
              cursor:
                !isCampaignValid || loadingbtn ? "not-allowed" : "pointer",
            }}
          >
            {loadingbtn ? (
              <CircularProgress
                size={20}
                style={{ alignSelf: "center" }}
                color="black"
              />
            ) : (
              "Salveaza campania & Genereaza playlist →"
            )}
          </button>
        </div>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loopPreviewLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    );
  }
);

export default AddLoopStep;
